'use client'

import { useEffect, useState } from 'react'
import { useUTMPersistence } from '@/hooks/useUTMPersistence'
import { getCookieConsent } from './CookieBanner'

declare global {
  interface Window {
    fbq: any
  }
}

export default function MetaPixel() {
  const { persistedParams } = useUTMPersistence()
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    // Only run on client side after hydration
    if (!isClient || typeof window === 'undefined' || !window.fbq) {
      return
    }

    // Check cookie consent before tracking
    const consent = getCookieConsent()
    if (consent !== 'accepted') {
      return
    }

    try {
      // Track page view with UTM parameters if available
      if (persistedParams && Object.keys(persistedParams).length > 0) {
        window.fbq('trackCustom', 'PageViewWithUTM', {
          utm_source: persistedParams.utm_source,
          utm_medium: persistedParams.utm_medium,
          utm_campaign: persistedParams.utm_campaign,
          utm_content: persistedParams.utm_content,
          utm_term: persistedParams.utm_term,
          fbclid: persistedParams.fbclid,
          landing_page: persistedParams.landingPage || window.location.href,
        })
      }
    } catch (error) {
      console.error('Meta Pixel tracking failed:', error)
    }
  }, [persistedParams, isClient])

  return null
}

// Export functions to track specific events
export const trackMetaEvent = (eventName: string, eventData?: object) => {
  if (typeof window === 'undefined' || !window.fbq) return

  // Check cookie consent before tracking
  const consent = getCookieConsent()
  if (consent !== 'accepted') return

  try {
    window.fbq('track', eventName, eventData)
  } catch (error) {
    console.error('Meta Pixel tracking error:', error)
  }
}

export const trackMetaConversion = (formData: any, utmData: any) => {
  if (typeof window === 'undefined' || !window.fbq) return

  // Check cookie consent before tracking
  const consent = getCookieConsent()
  if (consent !== 'accepted') return

  try {
    // Determine content name based on source
    let contentName = 'Energielösung Anfrage'
    let contentCategory = 'Energielösung'

    if (formData.source?.type === 'hauseigentumer') {
      contentName = 'Hauseigentümer Video Lead'
      contentCategory = 'Value Video'
    } else if (formData.selectedServices?.length > 0) {
      contentName = `${formData.selectedServices.join(' + ')} Anfrage`
      contentCategory = formData.selectedServices.join(', ')
    }

    // Track the main conversion event as Lead
    window.fbq('track', 'Lead', {
      content_name: contentName,
      content_category: contentCategory,
      value: 1,
      currency: 'EUR',

      // UTM tracking data
      utm_source: utmData.utm_source,
      utm_medium: utmData.utm_medium,
      utm_campaign: utmData.utm_campaign,
      utm_term: utmData.utm_term,
      utm_content: utmData.utm_content,
      landing_page: utmData.landingPage,

      // Source tracking
      lead_source: formData.source?.type || 'direct',

      // Form data (anonymized)
      service_type: formData.selectedServices?.join(','),
      postal_code: formData.plz,
      building_type: formData.gebaeudeart,

      // Enhanced data matching
      email: formData.email,
      phone: formData.phone,
      first_name: formData.firstName,
      last_name: formData.lastName,
    })

    console.log('Meta Lead conversion tracked successfully', {
      source: formData.source?.type,
      contentName,
      services: formData.selectedServices,
    })
  } catch (error) {
    console.error('Meta conversion tracking error:', error)
  }
}
